import React, { createContext, useContext, useState, useEffect } from 'react';

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const headers = {
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${API_BASE}/api/company/profile`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setCompanyProfile(data.data);
        setError(null);
      } else {
        console.error('Failed to fetch company profile:', response.status);
        setError('Failed to load company profile');
      }
    } catch (error) {
      console.error('Error fetching company profile:', error);
      setError('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const updateCompanyProfile = async (updatedData) => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const headers = {
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${API_BASE}/api/company/profile`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        const data = await response.json();
        setCompanyProfile(data.data);
        setError(null);
        return data.data;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update company profile');
      }
    } catch (error) {
      console.error('Error updating company profile:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const value = {
    companyProfile,
    loading,
    error,
    fetchCompanyProfile,
    updateCompanyProfile
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
